import type { ReactElement } from 'react'
import { Fragment } from 'react'
import { Navigate, Route } from 'react-router-dom'
import { AreaGate, InicioDaArea } from '../../core/areas/AreaGate'
import { PermissionGate } from '../../core/router/guards'
import { flattenNav } from '../../core/router/navConfig'
import { PlaceholderPage } from '../../shared/components/PlaceholderPage'
import { HubIndexRedirect } from '../../features/cadastros/CadastroHubLayout'
import { RedirecionarAnomaliaNoturna } from '../../features/frotaLeve/anomalias/RedirecionarAnomaliaNoturna'
import {
  CADASTROS_GERAIS_CONFIGS, CADASTROS_LOGISTICA_CONFIGS, CONJUNTOS_CONFIGS, CUSTOM_FORM_BASE_PATHS,
} from '../../features/cadastros/configs'
import {
  AbastecimentoPage, AdvertenciasPage, CadastrosHub, ColheitaAlertasPage, ColheitaDashboardPage,
  ColheitaProtheusPage, ColheitaShell, ConjuntosPage, ContractDetailPage, ContractFormPage, ContractsPage,
  DashboardPage, DiariasConferenciaPage, DiariasLancamentoPage, FrenteTrabalhoPage, FrentesPage,
  HospedagemDetailPage, HospedagemPage, JornadaDashboardPage, JornadaHistoricoPage, JornadaInfracoesPage,
  MapaPage, MensagensTecladoPage, NotasFiscaisPage, OutrosServicosTransportePage, PainelDescargaPage, PainelDiariasPage,
  PedagiosPage, PendenciasPage, PlanilhasPage, ResourceFormPage, ResourceListPage, ServicosPage,
  SubcontratosPage, TicketDetailPage, TicketFormPage, TransferenciasPage, TransporteMaquinaPage,
  ViagensAlertasPage, ViagensCobrancaNaoAtendimentoPage, ViagensDashboardPage, ViagensListaPage, ViagensShell, ViagensTempoUbsPage,
  ClientesHubPage, comPermissao, ProgramacaoClientePage, ProgramacaoClienteDetalhePage, GerarLinhasPage,
  AjusteEquipePage, LiberarPage, AnomaliaFrotaPage,
} from '../../core/router/telas'
import { cadastrosDaLogistica } from './cadastros'
import { logisticaNav } from './nav'

/** Registro path → componente dos itens do menu da Logística. Item sem entrada aqui
 *  cai no PlaceholderPage automaticamente. */
const PAGES: Record<string, ReactElement> = {
  '/logistica/dashboard': <DashboardPage />,
  '/logistica/pendencias': <PendenciasPage />,
  '/logistica/mapa': <MapaPage />,
  '/logistica/anomalia-frota': <AnomaliaFrotaPage />,
  '/logistica/tickets': <PainelDescargaPage />,
  '/logistica/shipments': <FrenteTrabalhoPage />,
  '/logistica/programacao-cliente': <ProgramacaoClientePage />,
  '/logistica/transporte-maquina': <TransporteMaquinaPage />,
  '/logistica/outros-servicos-transporte': <OutrosServicosTransportePage />,
  '/logistica/mensagens-teclado': <MensagensTecladoPage />,
  '/logistica/conjuntos/transferencias': <TransferenciasPage />,
  '/logistica/jornada': <JornadaDashboardPage />,
  '/logistica/diarias': <PainelDiariasPage />,
  '/logistica/advertencias': <AdvertenciasPage />,
  '/logistica/abastecimento': <AbastecimentoPage />,
  '/logistica/notas-fiscais': <NotasFiscaisPage />,
  // Sem esta entrada a rota plana do menu caía no PlaceholderPage antes da rota de
  // Pedágios declarada abaixo, e a tela abria como Em breve.
  '/logistica/pedagios': <PedagiosPage />,
  '/logistica/hospedagem': <HospedagemPage />,
}

/** Último segmento de um basePath ('/logistica/cadastros/duplas' → 'duplas'). */
const slug = (basePath: string) => basePath.split('/').filter(Boolean).pop() ?? basePath

/** Itens do menu que são hubs (rota própria com filhos): não entram como rota flat. */
const HUB_PATHS = new Set(['/logistica/cadastros', '/logistica/conjuntos', '/logistica/viagens', '/logistica/colheita'])

/** Portão de cada aba do hub de Cadastros, pela key que a aba declara. */
const permissaoDaAba = (to: string) => cadastrosDaLogistica.find((i) => i.to === to)?.permission ?? 'master_data.view'

/** As rotas da área Logística. Chamada como função, e não como componente, porque
 *  o React Router só aceita `<Route>` como filho direto de `<Routes>`. */
export function rotasDaLogistica(): ReactElement {
  return (
    <Route path="/logistica" element={<AreaGate area="logistica" />}>
      <Route index element={<InicioDaArea area="logistica" />} />

      {/* Itens 1:1 com rota. Os hubs são tratados à parte, com rotas aninhadas. */}
      {flattenNav(logisticaNav)
        .filter((item) => !HUB_PATHS.has(item.path))
        .map((item) => (
          <Route
            key={item.path}
            path={item.path}
            element={comPermissao(item, PAGES[item.path] ?? <PlaceholderPage title={item.label} />)}
          />
        ))}

      {/* Hub CADASTROS da Logística: os cadastros da operação, uma aba por cadastro,
          cada uma com o portão da própria key. O índice abre a primeira aba que a
          pessoa alcança. Os detalhes de contrato abrem em tela cheia. */}
      <Route path="/logistica/cadastros" element={<CadastrosHub itens={cadastrosDaLogistica} eyebrowKey="cadastros.cadastrosHub.eyebrow" />}>
        <Route index element={<HubIndexRedirect itens={cadastrosDaLogistica} />} />
        {/* Os cadastros gerais, de volta do Administrativo em 26/10/2026. */}
        {CADASTROS_GERAIS_CONFIGS.map((cfg) => (
          <Route
            key={cfg.basePath}
            path={slug(cfg.basePath)}
            element={<PermissionGate permission="master_data.view"><ResourceListPage config={cfg} /></PermissionGate>}
          />
        ))}
        {CADASTROS_LOGISTICA_CONFIGS.map((cfg) => (
          <Route
            key={cfg.basePath}
            path={slug(cfg.basePath)}
            element={<PermissionGate permission={permissaoDaAba(cfg.basePath)}><ResourceListPage config={cfg} /></PermissionGate>}
          />
        ))}
        <Route path="contratos" element={<PermissionGate permission="contracts.view"><ContractsPage /></PermissionGate>} />
        {/* Espelho do Protheus: lista só leitura, sem formulário nem detalhe em tela
            cheia. A permissão é a da rota: o servidor gateia por shipments.view. */}
        <Route path="subcontratos" element={<PermissionGate permission="shipments.view"><SubcontratosPage /></PermissionGate>} />
        <Route path="planilhas" element={<PermissionGate permission="shipments.view"><PlanilhasPage /></PermissionGate>} />
        <Route path="frentes" element={<PermissionGate permission="shipments.view"><FrentesPage /></PermissionGate>} />
        {/* Serviços (24/09/2026) e Clientes HUB (08/10/2026): grades próprias com
            diálogo, fora dos configs, com a key de Cadastros. */}
        <Route path="servicos" element={<PermissionGate permission="master_data.view"><ServicosPage /></PermissionGate>} />
        <Route path="clientes-hub" element={<PermissionGate permission="master_data.view"><ClientesHubPage /></PermissionGate>} />
      </Route>

      {/* Conjuntos (04/09/2026): a página de abas, uma rota por aba. Default =
          Caminhões. Os caminhos antigos sob Cadastros redirecionam. */}
      <Route path="/logistica/conjuntos" element={<PermissionGate permission="sets.view"><ConjuntosPage /></PermissionGate>}>
        <Route index element={<Navigate to="caminhoes" replace />} />
        {CONJUNTOS_CONFIGS.map((cfg) => (
          <Route key={cfg.basePath} path={slug(cfg.basePath)} element={<ResourceListPage config={cfg} />} />
        ))}
      </Route>

      {/* Criar e editar os cadastros gerais em tela dedicada. As Categorias foram
          para o Administrativo em 04/11/2026. */}
      {CADASTROS_GERAIS_CONFIGS
        .filter((cfg) => !CUSTOM_FORM_BASE_PATHS.has(cfg.basePath) && !cfg.readOnly)
        .map((cfg) => (
          <Fragment key={`form-${cfg.basePath}`}>
            <Route path={`${cfg.basePath}/new`} element={<PermissionGate permission="master_data.view"><ResourceFormPage config={cfg} /></PermissionGate>} />
            <Route path={`${cfg.basePath}/:uuid`} element={<PermissionGate permission="master_data.view"><ResourceFormPage config={cfg} /></PermissionGate>} />
          </Fragment>
        ))}

      {/* Criar e editar os cadastros da Logística em tela dedicada, fora dos hubs. */}
      {[...CADASTROS_LOGISTICA_CONFIGS, ...CONJUNTOS_CONFIGS].filter((cfg) => !cfg.readOnly).map((cfg) => (
        <Fragment key={`form-${cfg.basePath}`}>
          <Route path={`${cfg.basePath}/new`} element={<PermissionGate permission="sets.view"><ResourceFormPage config={cfg} /></PermissionGate>} />
          <Route path={`${cfg.basePath}/:uuid`} element={<PermissionGate permission="sets.view"><ResourceFormPage config={cfg} /></PermissionGate>} />
        </Fragment>
      ))}

      {/* Contratos: a lista mora no hub de Cadastros; o endereço antigo da lista
          leva a ela (Voltar e salvar de contrato caem aqui). Detalhe, criar e editar
          seguem em tela dedicada. */}
      <Route path="/logistica/contracts" element={<Navigate to="/logistica/cadastros/contratos" replace />} />
      <Route path="/logistica/contracts/new" element={<ContractFormPage />} />
      <Route path="/logistica/contracts/:uuid" element={<ContractDetailPage />} />
      <Route path="/logistica/contracts/:uuid/edit" element={<ContractFormPage />} />

      {/* Painel de Jornada: a segunda e a terceira tela do módulo, alcançadas pelo
          seletor no topo. Sub-rota solta não passa por `comPermissao`, que só
          enxerga a nav, e o portão vai à mão. */}
      <Route path="/logistica/jornada/historico" element={<PermissionGate permission="journey.view"><JornadaHistoricoPage /></PermissionGate>} />
      {/* Pendências saiu de dentro do Dashboard (29/09/2026), e o endereço antigo
          leva ao novo. */}
      <Route path="/logistica/dashboard/pendencias" element={<Navigate to="/logistica/pendencias" replace />} />
      {/* A Anomalia Noturna virou a Anomalia Frota (04/11/2026): o endereço antigo abre
          a aba Frota Leve, com a busca junto. */}
      <Route path="/logistica/frota-leve/anomalias" element={<RedirecionarAnomaliaNoturna />} />
      {/* Viagens (29/09/2026): o módulo em abas, uma rota por aba. Default = a
          tabela. O Histórico de antes virou o período da própria tabela. */}
      <Route path="/logistica/viagens" element={<PermissionGate permission="viagens.view"><ViagensShell /></PermissionGate>}>
        <Route index element={<Navigate to="lista" replace />} />
        <Route path="dashboard" element={<ViagensDashboardPage />} />
        <Route path="lista" element={<ViagensListaPage />} />
        <Route path="alertas" element={<ViagensAlertasPage />} />
        <Route path="tempo-ubs" element={<ViagensTempoUbsPage />} />
        <Route path="cobranca-nao-atendimento" element={<ViagensCobrancaNaoAtendimentoPage />} />
      </Route>
      <Route path="/logistica/viagens/historico" element={<Navigate to="/logistica/viagens/lista" replace />} />
      {/* Pedágios da Edenred (14/10/2026): uma tela com três abas, que moram na URL. */}
      <Route path="/logistica/pedagios" element={<PermissionGate permission="pedagios.view"><PedagiosPage /></PermissionGate>} />
      {/* Colheita (05/10/2026): uma rota por aba, como Viagens. Default = o Dashboard. */}
      <Route path="/logistica/colheita" element={<PermissionGate permission="colheita.view"><ColheitaShell /></PermissionGate>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<ColheitaDashboardPage />} />
        <Route path="protheus" element={<ColheitaProtheusPage />} />
        <Route path="alertas" element={<ColheitaAlertasPage />} />
      </Route>
      <Route path="/logistica/jornada/infracoes" element={<PermissionGate permission="journey.view"><JornadaInfracoesPage /></PermissionGate>} />
      {/* Painel de Diárias: as duas outras telas do módulo (Samuel, 03/09/2026). */}
      <Route path="/logistica/diarias/lancamento" element={<PermissionGate permission="diaria.view"><DiariasLancamentoPage /></PermissionGate>} />
      <Route path="/logistica/diarias/conferencia" element={<PermissionGate permission="diaria.view"><DiariasConferenciaPage /></PermissionGate>} />

      {/* Descargas: criar e detalhe em tela dedicada. */}
      <Route path="/logistica/tickets/new" element={<TicketFormPage />} />
      <Route path="/logistica/tickets/:uuid" element={<TicketDetailPage />} />

      {/* Programação do cliente (03/11/2026): o detalhe e cada ação em tela própria. */}
      <Route path="/logistica/programacao-cliente/:uuid" element={<PermissionGate permission="contracts.programacao_ver"><ProgramacaoClienteDetalhePage /></PermissionGate>} />
      <Route path="/logistica/programacao-cliente/:uuid/gerar" element={<PermissionGate permission="contracts.programacao_gerir"><GerarLinhasPage /></PermissionGate>} />
      <Route path="/logistica/programacao-cliente/:uuid/ajustar" element={<PermissionGate permission="contracts.programacao_gerir"><AjusteEquipePage /></PermissionGate>} />
      <Route path="/logistica/programacao-cliente/:uuid/liberar" element={<PermissionGate permission="contracts.programacao_gerir"><LiberarPage /></PermissionGate>} />

      {/* Hospedagem: detalhe do hotel em tela dedicada. */}
      <Route path="/logistica/hospedagem/:uuid" element={<HospedagemDetailPage />} />
    </Route>
  )
}
